import { ApplicationRef, ComponentFactoryResolver, ComponentRef, EmbeddedViewRef, Injectable, Injector, Type } from '@angular/core';
import { first } from 'rxjs/operators';

import { DialogModule } from '../dialog.module';
import { DialogComponent } from '../dialog/dialog.component';
import { DialogInjector } from '../injectors/dialog.injector';
import { DialogConfig } from '../models/dialog-config.model';
import { DialogRef } from '../models/dialog-ref.model';
import { LoadingDialogComponent } from '../templates/loading-dialog/loading-dialog.component';

/**
 * @description DialogService: this service is responsible for appending and removing the DialogComponent from the DOM.
 */
@Injectable({
	providedIn: DialogModule
})
export class DialogService {
	public dialogComponentRef: ComponentRef<DialogComponent>;

	constructor(
		private componentFactoryResolver: ComponentFactoryResolver,
		private appRef: ApplicationRef,
		private injector: Injector
	) { }


	/**
	 * @description open: opens the dialog; responsible for appending the dialog component to the DOM.
	 * @param {Type<any>} componentType: the template component which is passed into the DialogComponent.
	 * @param {DialogConfig} config: the config object for the DialogComponent which contains template component specific data.
	 */
	public open(componentType: Type<any>, config: DialogConfig = null): DialogRef {
		const ref = this.appendDialogComponentToBody(config);
		this.dialogComponentRef.instance.childComponentType = componentType;
		return ref;
	}

	public showLoading(): DialogRef {
		return this.open(LoadingDialogComponent);
	}

	/**
	 * @description close: closes the dialog; removes the dialog component from the DOM.
	 */
	public close(componentRef?: ComponentRef<DialogComponent>): void {
		this.removeDialogComponentFromBody(componentRef);
	}

	private appendDialogComponentToBody(config: DialogConfig): DialogRef {

		// create a map with the config
		const map = new WeakMap();
		map.set(DialogConfig, config);

		// add the DialogRef to dependency injection
		const dialogRef = new DialogRef();
		map.set(DialogRef, dialogRef);

		// Gets the factory that is specific to the DialogComponent
		const componentFactory = this.componentFactoryResolver.resolveComponentFactory(DialogComponent);
		// Create an instance of our component
		const componentRef = componentFactory.create(new DialogInjector(this.injector, map));

		// Append the component to the angular component tree
		this.appRef.attachView(componentRef.hostView);
		// Get the root DOM element of the DialogComponent and attach to the html body
		const rootDomElement = (componentRef.hostView as EmbeddedViewRef<any>).rootNodes[0] as HTMLElement;
		document.body.appendChild(rootDomElement);
		this.dialogComponentRef = componentRef;
		dialogRef.componentRef = componentRef;

		// we want to know when somebody calls the close method
		dialogRef.afterClosed.pipe(first()).subscribe(() => {
			// close the dialog
			this.removeDialogComponentFromBody(dialogRef.componentRef);
		});

		this.dialogComponentRef.instance.onClose.pipe(first()).subscribe(() => {
			this.removeDialogComponentFromBody(dialogRef.componentRef);
		});
		// return the dialogRef
		return dialogRef;
	}

	private removeDialogComponentFromBody(componentRef?: ComponentRef<DialogComponent>): void {
		if (componentRef) {
			this.appRef.detachView(componentRef.hostView);
			componentRef.destroy();
		} else if (this.dialogComponentRef) {
			this.appRef.detachView(this.dialogComponentRef.hostView);
			this.dialogComponentRef.destroy();
		}
	}
}
